import { useNavigate, useParams } from 'react-router-dom';
import '../Styles/ProductEnquiryStyles.css'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useState } from 'react';
import { API_URL } from '../../../../helper/api_url';
import { ApiRequestMethod } from '../../../../helper/api_calls';
import toast from 'react-hot-toast';
import { IoMdClose } from 'react-icons/io';
import SpinnerLoader from '../../../../assets/loader/SpinnerLoader';

const ProductEnquiryView = () => {

    const {productname} = useParams();


    const ValidationFormNameWarning = {
        VALIDATE_NAME: "Name is required",
        VALIDATE_NAME_NUMBERS: "Name should not contain numbers and symbols",
        VALIDATE_NAME_LENGTH: "Name should be at least 3 characters",
        VALIDATE_NAME_MAX: "Name should not exceed 26 characters",
        VALIDATE_WHITE_SPACE: "Name should not contain blank spaces"
    }

    const ValidationFormEmailWarning = {
        VALIDATE_EMAIL: "Email is required",
        VALIDATE_EMAIL_INVALID: "Invalid email address",
        NUMBER_NOT_ALLOWED_EMAIL: "Email should not start with a number",
        SPACE_EMAIL_VALIDATION: 'Email should not contain blank spaces'
    }


    const [showLoader,setShowLoader] = useState(false)

    const navigate = useNavigate();



    const formik = useFormik({
        initialValues: {
            name: '',
            email: '',
            phone: '',
            companyName: '',
            companyGst: '',
            description: ''
        },
        validationSchema: Yup.object({
            name: Yup.string()
                .required(ValidationFormNameWarning.VALIDATE_NAME)
                .test("notEmpty", ValidationFormNameWarning.VALIDATE_NAME_LENGTH, (value) => value.trim() !== "")
                .matches(/^[A-Za-z\s]+$/, ValidationFormNameWarning.VALIDATE_NAME_NUMBERS)
                .matches(/^(?!.*\s{2,}).*$/, "Name should not contain consecutive spaces.")
                .min(3, ValidationFormNameWarning.VALIDATE_NAME_LENGTH)
                .max(30, ValidationFormNameWarning.VALIDATE_NAME_MAX),
            email: Yup.string().email(ValidationFormEmailWarning.VALIDATE_EMAIL_INVALID).required(ValidationFormEmailWarning.VALIDATE_EMAIL)
                .matches(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/, ValidationFormEmailWarning.VALIDATE_EMAIL_INVALID).max(36, ValidationFormEmailWarning.VALIDATE_EMAIL_INVALID)
                .test(
                    'no-extra-dots',
                    ValidationFormEmailWarning.VALIDATE_EMAIL_INVALID,
                    (value) => value && /^(?!.*\.\.)(?=.*\.com$).*$/.test(value)
                )
                .test(
                    'no-starting-number',
                    ValidationFormEmailWarning.NUMBER_NOT_ALLOWED_EMAIL,
                    (value) => !/^\d/.test(value)
                ).test(
                    'no-hyphen',
                    ValidationFormEmailWarning.VALIDATE_EMAIL_INVALID,
                    (value) => !/\-/.test(value)
                ).test(
                    'no-leading-space',
                    ValidationFormEmailWarning.SPACE_EMAIL_VALIDATION,
                    (value) => value && value.trim() === value
                ),
            phone: Yup.string().matches(/^\d{10}$/, 'Phone must be 10 digits').required('Phone is required'),
        }),
        onSubmit: async (values, { resetForm }) => {
            setShowLoader(true);


            const data = {
                product_name: productname,
                phone: values.phone,
                email: values.email,
                name: values.name,
                ...(values.companyName && { company_name: values.companyName }),
                ...(values.companyGst && { company_gst: values.companyGst }),
                ...(values.description && { description: values.description })
            }

            const payload = {
                method: "post",
                data: data,
                url: API_URL?.product_registration
            };

            try {
                const response = await ApiRequestMethod(payload);

                if (response?.status === 201) {
                    toast.success("Your enquiry has been received. We’ll be in touch within 24 hours.");
                    navigate(`/products/${productname}`)
                    resetForm();
                    
                } 
            

            } catch (error) {
                toast.error('Something went wrong. Please try again.');
                setTimeout(() => {  
                }, 100);
            } finally {
                setShowLoader(false);
            }
        }


    });

    if(showLoader){
        return <SpinnerLoader/>
    }


    return (
        <div className='product-enquiry-main-view-container'>
            <div className='product-enquiry-main-view-container-wrapup' data-aos="fade-up">
                <div className='form-title-close-button'>
                    <div className="form-title">
                        Product <span className="highlight">&nbsp;Enquiry</span>
                    </div>
                </div>
                <form className="form-body" onSubmit={formik.handleSubmit}>
                    <div className="form-group">
                        <label>Name <span className="required">*</span></label>
                        <input
                            type="text"
                            name="name"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.name}
                            placeholder="e.g. John Doe"
                        />
                        {formik.touched.name && formik.errors.name && <div className="error-text">{formik.errors.name}</div>}
                    </div>
                    <div className="form-group">
                        <label>Email <span className="required">*</span></label>
                        <input
                            type="email"
                            name="email"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.email}
                            placeholder="e.g. johndoe@gmail.com"
                        />
                        {formik.touched.email && formik.errors.email && <div className="error-text">{formik.errors.email}</div>}
                    </div>
                    <div className="form-group">
                        <label>Phone Number <span className="required">*</span></label>
                        <input
                            type="tel"
                            name="phone"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.phone}
                            placeholder="e.g. 9876543210"
                        />
                        {formik.touched.phone && formik.errors.phone && <div className="error-text">{formik.errors.phone}</div>}
                    </div>
                    <div className="form-group">
                        <label>Company Name</label>
                        <input
                            type="text"
                            name="companyName"
                            onChange={formik.handleChange}
                            value={formik.values.companyName}
                            placeholder="e.g. IINVSYS Pvt.ltd"
                        />
                    </div>
                    <div className="form-group">
                        <label>Company Gst</label>
                        <input
                            type="text"
                            name="companyGst"
                            onChange={formik.handleChange}
                            value={formik.values.companyGst}
                            placeholder="e.g. 22AAxxxxxxxxxxx"
                        />
                    </div>
                    <div className="form-group">
                        <label>Description</label>
                        <textarea
                            name="description"
                            onChange={formik.handleChange}
                            value={formik.values.description}
                            placeholder="e.g. Can i know about this product"
                            rows="4"
                        />
                    </div>

                    <span className='product-page-view-text-user-info'>Submit your enquiry and we’ll respond as soon as possible.</span>


                    <div className='product-page-view-main-container-button-container'>
                        <div class="product-page-view-main-container-button-container">
                            <button class="warranty-page-submit-button" type="submit">
                                <span>Submit</span>
                            </button>
                        </div>
                    </div>

                </form>
            </div>
        </div>
    )
}

export default ProductEnquiryView;



